import type { Asset } from "../shared/types";
import { bilingual, lang, text } from "./i18n";

const TEXT = bilingual({
  en: { failed: "Something went wrong. Please try again." },
  es: { failed: "Algo ha fallado. Inténtalo de nuevo." },
});
export async function api<T = any>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  // The studio answers (errors, emails it sends) in the language the visitor chose.
  const res = await fetch(`/api${url}`, {
    ...options,
    headers: {
      ...(options?.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      "Accept-Language": lang(),
      ...options?.headers,
    },
  });
  const data = await res.json();
  if (!res.ok)
    throw Object.assign(new Error(data.error || text(TEXT).failed), {
      status: res.status,
      code: data.code as string | undefined,
    });
  return data;
}
/** The proxy in front of the studio refuses requests over 100 MB, so files go up in batches below that. */
const BATCH_BYTES = 90 * 1024 * 1024;
/**
 * Adds files to a project, a batch per request. `ids` holds one retry-safe id
 * per batch; keeping it while the files stay the same makes a retry add
 * nothing twice.
 */
export async function uploadFiles(
  projectId: string,
  files: File[],
  ids?: string[],
) {
  const batches: File[][] = [];
  for (const f of files) {
    const last = batches.at(-1);
    if (
      last &&
      last.length < 10 &&
      last.reduce((n, x) => n + x.size, 0) + f.size <= BATCH_BYTES
    )
      last.push(f);
    else batches.push([f]);
  }
  const assets: Asset[] = [];
  let warning: string | undefined;
  for (const [i, batch] of batches.entries()) {
    const data = new FormData();
    if (ids) data.append("requestId", (ids[i] ??= crypto.randomUUID()));
    for (const f of batch) data.append("files", f);
    const result = await api<{ assets: Asset[]; warning?: string }>(
      `/projects/${projectId}/assets`,
      { method: "POST", body: data },
    );
    assets.push(...result.assets);
    warning = result.warning || warning;
  }
  return { assets, warning };
}
export const post = <T = any>(url: string, data?: unknown) =>
  api<T>(url, { method: "POST", body: JSON.stringify(data || {}) });
export const patch = <T = any>(url: string, data: unknown) =>
  api<T>(url, { method: "PATCH", body: JSON.stringify(data) });
export const formatTime = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
export const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
